import { useCallback, useState } from 'react';
import { Modal, message } from 'antd';
import { useDeleteDocumentMutation } from '../../store/api/documentsApi';
import {
  downloadDocument,
  fetchDocumentObjectUrl,
  documentPreviewEndpoint,
  viewDocument,
} from '../../utils/documentDownload';

export type DocumentActionRow = {
  id: number;
  fileType?: string | null;
  fileName?: string;
  content?: { originalName?: unknown } | null;
};

export function useDocumentActions(onRefresh?: () => void) {
  const [deleteDocument] = useDeleteDocumentMutation();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewRevoke, setPreviewRevoke] = useState<(() => void) | null>(null);

  const closePreview = useCallback(() => {
    previewRevoke?.();
    setPreviewVisible(false);
    setPreviewImage('');
    setPreviewRevoke(null);
  }, [previewRevoke]);

  const handlePreview = useCallback(async (row: DocumentActionRow) => {
    try {
      if ((row.fileType || 'file') !== 'photo') {
        await viewDocument(row.id);
        return;
      }
      const { url, revoke } = await fetchDocumentObjectUrl(row.id, documentPreviewEndpoint(row.fileType));
      setPreviewImage(url);
      setPreviewRevoke(() => revoke);
      setPreviewVisible(true);
    } catch {
      message.error('Ошибка при загрузке файла');
    }
  }, []);

  const handleDownload = useCallback(async (row: DocumentActionRow) => {
    try {
      await downloadDocument(row.id, {
        id: row.id,
        content: { originalName: row.fileName ?? row.content?.originalName },
      });
    } catch {
      message.error('Ошибка при скачивании файла');
    }
  }, []);

  const handleDelete = useCallback(
    (row: DocumentActionRow) => {
      Modal.confirm({
        title: 'Удаление документа',
        content: 'Вы уверены, что хотите удалить этот документ?',
        okText: 'Удалить',
        cancelText: 'Отмена',
        onOk: async () => {
          try {
            await deleteDocument(row.id).unwrap();
            message.success('Документ удален');
            onRefresh?.();
          } catch {
            message.error('Ошибка при удалении документа');
          }
        },
      });
    },
    [deleteDocument, onRefresh]
  );

  return {
    previewVisible,
    previewImage,
    closePreview,
    handlePreview,
    handleDownload,
    handleDelete,
  };
}
